/**
 * Frontend error model aligned to the DIRECT-011 public error envelope
 * (APP-001 §12). The authoritative contract is the OpenAPI `PublicError`
 * schema; these types derive from it rather than redefining field names.
 *
 * `ApiError` is the single normalized error type thrown by the API client.
 * It always preserves the server `request_id` (never dropped or logged raw)
 * and exposes a small set of helpers for presentation and retry decisions.
 */
import type { components } from './generated/openapi';

export type PublicErrorCode = components['schemas']['PublicErrorCode'];
export type PublicErrorType = components['schemas']['PublicErrorType'];
export type ActionType = components['schemas']['ActionType'];

export interface ErrorAction {
  type: ActionType;
  url?: string | null;
  label?: string | null;
  required_plan?: string | null;
}

export interface FieldError {
  field: string;
  code: string;
  message: string;
}

export interface ApiError {
  /** HTTP status (0 for network failures). */
  status: number;
  code: PublicErrorCode | 'NETWORK_ERROR' | 'PARSE_ERROR' | 'UNKNOWN';
  type: PublicErrorType | 'network_error' | 'unknown';
  message: string;
  /** Server request ID — preserved for diagnostics, never logged raw with secrets. */
  requestId?: string;
  retryable: boolean;
  action?: ErrorAction;
  fieldErrors?: FieldError[];
  retryAfterSeconds?: number | null;
  /** Raw details object (safe, server-provided; e.g. required_scope). */
  details?: Record<string, unknown>;
  /** True when the request never reached the server (offline/DNS/aborted). */
  isNetworkError: boolean;
  /** True when the request was aborted via AbortController. */
  isAborted: boolean;
}

/** Narrow a thrown value into an ApiError (defensive; unknowns become UNKNOWN). */
export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'status' in value &&
    'code' in value &&
    'isNetworkError' in value
  );
}

/** Build a network-level ApiError (offline, DNS, connection refused). */
export function networkError(message = 'Network request failed.'): ApiError {
  return {
    status: 0,
    code: 'NETWORK_ERROR',
    type: 'network_error',
    message,
    retryable: true,
    isNetworkError: true,
    isAborted: false,
  };
}

/** Build an ApiError for an aborted (cancelled) request. */
export function abortedError(): ApiError {
  return {
    status: 0,
    code: 'NETWORK_ERROR',
    type: 'network_error',
    message: 'Request was cancelled.',
    retryable: false,
    isNetworkError: false,
    isAborted: true,
  };
}

/** Build an ApiError for a response whose body could not be parsed. */
export function parseError(status: number, requestId: string | undefined): ApiError {
  return {
    status,
    code: 'PARSE_ERROR',
    type: 'unknown',
    message: 'The server returned a response that could not be read.',
    requestId,
    retryable: false,
    isNetworkError: false,
    isAborted: false,
  };
}

/**
 * Parse an HTTP response into an ApiError. Reads the DIRECT-011 envelope:
 *
 *   { error: { code, message, type, request_id, retryable, action?, field_errors?, … } }
 *
 * If the body is missing or malformed, degrades gracefully to a generic
 * internal-error ApiError while still preserving any X-Request-ID header.
 */
export async function parseApiError(response: Response): Promise<ApiError> {
  const headerRequestId = response.headers.get('x-request-id') ?? undefined;
  let body: unknown = undefined;
  try {
    const text = await response.text();
    if (text) body = JSON.parse(text);
  } catch {
    return parseError(response.status, headerRequestId);
  }

  const envelope = body as { error?: Record<string, unknown> } | undefined;
  const err = envelope?.error;
  if (!err || typeof err !== 'object') {
    return {
      status: response.status,
      code: 'UNKNOWN',
      type: 'unknown',
      message: fallbackMessageForStatus(response.status),
      requestId: headerRequestId,
      retryable: response.status >= 500,
      isNetworkError: false,
      isAborted: false,
    };
  }

  const action = err.action as ErrorAction | undefined;
  const fieldErrors = err.field_errors as FieldError[] | undefined;
  return {
    status: response.status,
    code: (err.code as PublicErrorCode) ?? 'UNKNOWN',
    type: (err.type as PublicErrorType) ?? 'unknown',
    message: typeof err.message === 'string' ? err.message : fallbackMessageForStatus(response.status),
    requestId: typeof err.request_id === 'string' ? err.request_id : headerRequestId,
    retryable: Boolean(err.retryable),
    action,
    fieldErrors,
    retryAfterSeconds: (err.retry_after_seconds as number | null | undefined) ?? null,
    details: err.details as Record<string, unknown> | undefined,
    isNetworkError: false,
    isAborted: false,
  };
}

function fallbackMessageForStatus(status: number): string {
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to perform this action.';
  if (status === 404) return 'The requested resource was not found.';
  if (status === 429) return 'Too many requests. Please slow down and try again shortly.';
  if (status >= 500) return 'A server error occurred. Please try again shortly.';
  return 'An unexpected error occurred.';
}

/* ----------------------------------------------------------------------------
 * Presentation helpers (APP-001 §12).
 * ------------------------------------------------------------------------- */

/** True for authentication/session failures (401). */
export function isAuthError(err: ApiError): boolean {
  return err.status === 401 || err.code === 'MISSING_SESSION' || err.code === 'INVALID_SESSION' || err.code === 'EXPIRED_SESSION';
}

/** True for authorization failures (403): disabled account, scope, plan gate. */
export function isAuthzError(err: ApiError): boolean {
  return err.status === 403;
}

/** True for the backend synchronization-pending conflict (409, retryable). */
export function isSyncPendingError(err: ApiError): boolean {
  return err.code === 'ACCOUNT_SYNC_PENDING';
}

/** True for quota/rate-limit failures (429). */
export function isQuotaOrRateLimitError(err: ApiError): boolean {
  return err.type === 'quota_error' || err.type === 'rate_limit_error' || err.status === 429;
}

/** True for transient server failures worth a bounded retry (5xx, service_error). */
export function isTransientServerError(err: ApiError): boolean {
  return err.status >= 502 || err.type === 'service_error';
}

/** True for validation errors that must never be retried automatically. */
export function isValidationError(err: ApiError): boolean {
  return err.status === 400 || err.status === 422 || err.type === 'validation_error';
}
